import { motion } from "motion/react";
import { SectionHeading } from "../ui/SectionHeading";
import { Reveal } from "../ui/Reveal";
import { marquee, skillGroups } from "../../data/content";

function SkillIcon({ name }: { name: string }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (name === "code")
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" {...common}>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    );
  if (name === "server")
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" {...common}>
        <rect x="3" y="4" width="18" height="7" rx="2" />
        <rect x="3" y="13" width="18" height="7" rx="2" />
        <line x1="7" y1="7.5" x2="7.01" y2="7.5" />
        <line x1="7" y1="16.5" x2="7.01" y2="16.5" />
      </svg>
    );
  if (name === "palette")
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" {...common}>
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.83-.44-1.12-.29-.29-.44-.65-.44-1.13a1.64 1.64 0 0 1 1.67-1.67h2c3.05 0 5.55-2.5 5.55-5.55C21.97 6.01 17.46 2 12 2Z" />
        <circle cx="7.5" cy="11" r=".6" fill="currentColor" stroke="none" />
        <circle cx="12" cy="7.5" r=".6" fill="currentColor" stroke="none" />
        <circle cx="17" cy="10.5" r=".6" fill="currentColor" stroke="none" />
      </svg>
    );
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" {...common}>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
      <path d="M19 15l.9 2.4L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15Z" />
    </svg>
  );
}

function SkillBar({ name, level, delay }: { name: string; level: number; delay: number }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="text-white/80">{name}</span>
        <span className="font-mono text-xs text-muted">{level}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary to-accent"
          initial={{ width: 0 }}
          whileInView={{ width: `${level}%` }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay }}
        />
      </div>
    </div>
  );
}

export function Skills() {
  return (
    <section id="skills" className="relative overflow-hidden bg-ink-2">
      {/* Marquee */}
      <div className="relative border-y border-white/8 py-5">
        <div className="flex w-max animate-marquee gap-12 whitespace-nowrap">
          {[...marquee, ...marquee].map((item, i) => (
            <span key={i} className="flex items-center gap-12">
              <span className="font-display text-2xl font-semibold text-white/15 transition-colors hover:text-white/60">
                {item}
              </span>
              <span className="text-primary/40">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="section">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <SectionHeading eyebrow="02 · Skills" title="A toolkit built for" accent="craft & scale." />

          <div className="grid gap-6 md:grid-cols-3">
            {skillGroups.map((group, gi) => (
              <Reveal key={group.title} delay={gi * 0.12}>
                <div className="card-glow group h-full rounded-3xl border border-white/10 bg-surface/60 p-8 backdrop-blur hover:border-white/20">
                  <div className="mb-6 flex items-center gap-3">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-primary/25 to-accent/25 text-primary transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                      <SkillIcon name={group.icon} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-white">
                        {group.title}
                      </h3>
                      <p className="text-xs text-muted">{group.blurb}</p>
                    </div>
                  </div>
                  <div className="space-y-5">
                    {group.skills.map((s, si) => (
                      <SkillBar key={s.name} name={s.name} level={s.level} delay={si * 0.1} />
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
